import { z } from "zod";

/**
 * `PRICE-NEGOTIATION` N1-CONTRACT (D-1102-14, A-H rulings N-1 to N-4): **the queue read and the answers' bodies** (the doors are `VehicleQueueController`'s, `P1-CAT-047`). One definition for the
 * client's join and leave (`POST` / `DELETE /api/v1/me/vehicles/:vehicleId/queue`, `GET` for his own place), and the dealership's board
 * and answers (`GET /api/v1/me/queue-board`, `POST /api/v1/me/vehicles/:vehicleId/queue/answer`). Serves both modes: the firm-price car goes
 * live through these doors; the negotiable car's offers board is N2's.
 *
 * **What the dealer sees of an entry, ONLY:** whether the client is eligible (yes/no), whether a deposit is declared (yes/no), how long
 * the entry has waited and when the front position lapses. Never an income, a debt, a credit strength, a grade, a name or a phone: the
 * projection is its own type (`dealerQueueEntrySchema`), and a strict schema refuses any other key.
 *
 * Every figure is served; the browser computes nothing.
 */

/** A client's place on one car. `front` is the position the dealer's question was asked about; `queued` waits behind it. */
export const QUEUE_PLACE_STATES = ["none", "queued", "front"] as const;

const buildSchemas = (object: typeof z.strictObject) => {
  const join = object({
    entryId: z.uuid(),
    /** `queued` on a first join; `front` if the entry already stood at the front when asked again. */
    status: z.enum(["queued", "front"]),
    /** True when the client was already in this queue: no second place was made. */
    alreadyJoined: z.boolean(),
  });
  const leave = object({ left: z.boolean() });
  const place = object({
    status: z.enum(QUEUE_PLACE_STATES),
    /** How many clients wait ahead of him on this car; `0` at the front or outside the queue. */
    ahead: z.number().int().min(0),
    /** When his hold on the front position lapses (ISO instant); `null` unless he is at the front. A hold on his POSITION, never on the car. */
    frontExpiresAt: z.string().nullable(),
    /** The countdown, served: whole minutes left on that hold, from the database's clock, never below `0`; `null` unless he is at the front. The screen computes nothing. */
    holdRemainingMinutes: z.number().int().min(0).nullable(),
  });
  const entry = object({
    vehicleId: z.uuid(),
    title: z.string().min(1),
    priceAmount: z.number().int(),
    entryId: z.uuid(),
    /** `front`: the client was promoted and holds the position; `awaiting_answer`: the head of the queue, the dealer's question is open. */
    status: z.enum(["front", "awaiting_answer"]),
    /** Whether the client's latest prequalification says he is within what banks accept. */
    eligible: z.boolean(),
    /** Whether the client SIMULATED a deposit on this car (a simulation on this car sets one aside). The screen word is « Apport simulé » (PROPOSED). A simulation is not a declaration: the key says so. */
    depositSimulated: z.boolean(),
    /** Whole hours the entry has waited, from the database's clock. */
    ageHours: z.number().int().min(0),
    /** When the front position lapses (ISO instant); `null` while the dealer's question is still open. */
    frontExpiresAt: z.string().nullable(),
    /** The countdown, served: whole minutes left on the front position, from the database's clock; `null` while the dealer's question is still open. */
    holdRemainingMinutes: z.number().int().min(0).nullable(),
  });
  const board = object({ data: z.array(entry) });
  const answer = object({
    answer: z.enum(["disponible", "indisponible"]),
    /** `disponible`: true when the head of the queue was promoted to the front by this answer. */
    promoted: z.boolean(),
    /** `indisponible`: how many clients were told the car is not available. */
    collapsedCount: z.number().int().min(0),
  });
  /** `GET /api/v1/me/queue-front`'s entry (Q1c, lane F): one live front position of the client's, as `CLIENT_FRONT_ENTRIES_SQL` serves it. The ISO instants are the database's text. */
  const frontEntry = object({
    queueEntryId: z.string().min(1),
    vehicleId: z.string().min(1),
    vehicleTitle: z.string().min(1),
    priceAmount: z.number().int(),
    frontAt: z.string().min(1),
    frontExpiresAt: z.string().min(1),
  });
  const front = object({ data: z.array(frontEntry) });
  return { join, leave, place, entry, board, answer, frontEntry, front };
};
const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

/** `POST /api/v1/me/vehicles/:vehicleId/queue`: no body (« Je suis intéressé » carries nothing but the car in the path). Strict: any key is a mismatch. */
export const queueJoinBodySchema = z.strictObject({});

export const queueJoinAnswerSchema = strict.join;
export const queueLeaveAnswerSchema = strict.leave;
export const queuePlaceAnswerSchema = strict.place;
export const dealerQueueEntrySchema = strict.entry;
export const dealerQueueBoardSchema = strict.board;
export const queueAnswerResultSchema = strict.answer;
export const queueFrontEntrySchema = strict.frontEntry;
export const queueFrontAnswerSchema = strict.front;

/** The dealer's answer to the open question. Strict: any other key is a mismatch, the field named. */
export const queueAnswerBodySchema = z.strictObject({ answer: z.enum(["disponible", "indisponible"]) });

/** The clients' and the dealer's screens read the same answers with unknown keys tolerated. */
export const dealerQueueBoardLenientReadSchema = loose.board;
export const queuePlaceLenientReadSchema = loose.place;
export const queueJoinLenientReadSchema = loose.join;
export const queueLeaveLenientReadSchema = loose.leave;
export const queueFrontLenientReadSchema = loose.front;

export type QueueJoinAnswer = z.infer<typeof queueJoinAnswerSchema>;
export type QueuePlaceAnswer = z.infer<typeof queuePlaceAnswerSchema>;
export type DealerQueueEntry = z.infer<typeof dealerQueueEntrySchema>;
export type DealerQueueBoard = z.infer<typeof dealerQueueBoardSchema>;
export type QueueAnswerResult = z.infer<typeof queueAnswerResultSchema>;
export type QueueFrontEntry = z.infer<typeof queueFrontEntrySchema>;
export type QueueFrontAnswer = z.infer<typeof queueFrontAnswerSchema>;
